import type { ProvenanceEntryKind } from "@ondo/shared";

import { HISTORY_COPY } from "./provenance.constants.ts";
import type { HistoryKindChipModel } from "./provenance.model.ts";

/**
 * What sort of history this is going to be.
 *
 * A radio group and not a row of toggles, because exactly one is true and the
 * arrow keys should move between them. `aria-checked` rather than
 * `aria-pressed`: these are one choice, not nine independent switches.
 *
 * NOTHING IS CHECKED WHEN THE MODAL OPENS, which is the one place this departs
 * from the design. An entry is frozen the moment it is saved and can afterwards
 * only be withdrawn, so a preselected chip is a default on a one-way door — a
 * collector who fills in a sale under a chip they never looked at gets an
 * exhibition on their record and no way to edit it.
 *
 * The chip stays live after a choice so it can be changed. `HistoryFields`
 * swaps the fields under it and nothing typed is thrown away — see `DraftEntry`.
 *
 * THE CHIPS ARE HANDED TO IT, which they were not before the ninth kind. Whether
 * a chip may be pressed is now a rule about the work — an authorship claim needs
 * an artist the registry holds, because authority to answer is held over one —
 * and `historyKindChips` is where that rule lives. An unavailable chip is
 * DISABLED WITH ITS REASON rather than dropped from the row: a collector who
 * cannot see the control cannot tell a withheld feature from an absent one, and
 * this is the one the registry most wants them to ask for.
 */
export function HistoryKindChips({
  chips,
  onChoose,
}: {
  chips: HistoryKindChipModel[];
  onChoose: (kind: ProvenanceEntryKind) => void;
}) {
  return (
    <>
      <div className="history__kinds" role="radiogroup" aria-label={HISTORY_COPY.kindLegend}>
        {chips.map((chip) => (
          <button
            key={chip.kind}
            type="button"
            role="radio"
            aria-checked={chip.chosen}
            disabled={chip.unavailable !== null}
            // The reason is on the control as well as under the row, so a
            // pointer user meets it where they pressed.
            {...(chip.unavailable === null ? {} : { title: chip.unavailable })}
            className={`history__kind${chip.chosen ? " history__kind--on" : ""}${
              chip.unavailable === null ? "" : " history__kind--off"
            }`}
            style={{ "--event": `var(--event-${chip.tone})` } as React.CSSProperties}
            onClick={() => onChoose(chip.kind)}
          >
            {/* The glyph repeats the label to a screen reader otherwise — the
                chip's accessible name is the words beside it. */}
            <span aria-hidden="true">{chip.glyph}</span>
            {chip.label}
          </button>
        ))}
      </div>

      {/* OUTSIDE the radiogroup, deliberately: a `radiogroup` owns radios, and a
          paragraph among them is content assistive technology may skip. Said in
          full because a `title` is a hover and this is a sentence somebody has
          to be able to read. One per withheld chip; there is at most one today
          and this does not assume it. */}
      {chips
        .filter((chip) => chip.unavailable !== null)
        .map((chip) => (
          <p className="history__kind-reason" key={`${chip.kind}-reason`}>
            {chip.unavailable}
          </p>
        ))}
    </>
  );
}
